// Serializes onboarding tours that become eligible at the same time. Each tour asks for the
// floor and only the holder may open, so two spotlights can never dim the screen at once and
// the order is deterministic (lowest `TOUR_PRIORITY` first) instead of depending on which
// component happened to mount first.
//
// Module-level state on purpose: the tours live on unrelated subtrees (the dashboard layout
// hosts the sidebar one, the order page hosts the order ones), so a React context would have
// to wrap the whole app to coordinate them.

type Listener = () => void;

const waiting = new Map<string, number>();
const listeners = new Set<Listener>();
let holder: string | null = null;
// Whether the holder has actually opened. Until it has, the grant is provisional and can still
// be reassigned; once a tour is on screen it is never preempted.
let holderStarted = false;

const notify = (): void => {
  listeners.forEach((listener) => listener());
};

/**
 * Hands the floor to the lowest-priority waiting tour.
 *
 * Tours register from effects, and a page's own effects run before the shell's, so the first
 * registration is not the one with the lowest priority number: on an order page the order tours
 * queue up before the sidebar tour does. Keeping the first requester would make the declared
 * order depend on React's effect order instead of `TOUR_PRIORITY`, so a provisional holder that
 * has not opened yet is reconsidered. A holder that is already on screen keeps the floor.
 */
const grantFloor = (): void => {
  if (holder && holderStarted && waiting.has(holder)) return;

  const [next] = [...waiting.entries()].sort(
    ([idA, priorityA], [idB, priorityB]) => priorityA - priorityB || idA.localeCompare(idB)
  );
  const nextHolder = next?.[0] ?? null;
  if (nextHolder === holder) return;

  holder = nextHolder;
  holderStarted = false;
};

export const requestTourFloor = (tourId: string, priority: number): void => {
  if (waiting.get(tourId) === priority) return;
  waiting.set(tourId, priority);
  grantFloor();
  notify();
};

/** Marks the holder as on screen, so a later lower-priority registration cannot preempt it. */
export const markTourStarted = (tourId: string): void => {
  if (holder !== tourId || holderStarted) return;
  holderStarted = true;
};

export const releaseTourFloor = (tourId: string): void => {
  const wasWaiting = waiting.delete(tourId);
  if (!wasWaiting && holder !== tourId) return;
  if (holder === tourId) {
    holder = null;
    holderStarted = false;
  }
  grantFloor();
  notify();
};

export const subscribeTourFloor = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getTourFloorHolder = (): string | null => holder;
